import React from "react";
import { Card, Typography, Tag, Divider } from "antd";
import GasDocsTable from "../GasDocsTable";
import { gases } from "../../../data";

const { Paragraph, Text, Title } = Typography;

export default function OxygenSection() {
  const gas = gases.find((g) => g.key === "oxygen");

  return (
    <Card
      bordered
      style={{
        borderRadius: 12,
        padding: 16,
      }}
      title={
        <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Tag
            color={gas.color}
            style={{
              margin: 0,
              display: "flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            {gas.icon}
            {gas.label}
          </Tag>
        </span>
      }
    >
      {/* --- Таблица параметров --- */}
      <GasDocsTable gasKey="oxygen" />
      <Divider />
      {/* --- 1. Назначение --- */}
      <Paragraph>
        <Text strong>Область применения:</Text> кислород используется для
        дыхательных аппаратов, наркозных систем, ингаляций и поддержания
        жизнедеятельности пациентов. Он является базовым медицинским газом,
        обеспечивающим работу операционных, палат интенсивной терапии,
        реанимаций, родильных и послеоперационных отделений.
      </Paragraph>

      <Paragraph>
        <Text strong>Нормативная база:</Text> расчёт и проектирование выполняются
        в соответствии с <Text code>СП 158.13330.2014</Text> — «Здания и
        помещения медицинских организаций». Расход кислорода определяется по
        количеству точек потребления, коэффициенту использования и времени работы
        системы.
      </Paragraph>

      <Divider />

      {/* --- 2. Формула --- */}
      <Paragraph>
        <Text strong>Формула расчёта:</Text>
      </Paragraph>

      <Paragraph
        code
        style={{
          fontSize: 16,
          padding: "8px 12px",
          borderRadius: 6,
          display: "inline-block",
        }}
      >
        {gas.formulaSymbolic}
      </Paragraph>

      <Paragraph type="secondary" style={{ marginTop: 6 }}>
        {gas.formulaText}
      </Paragraph>

      <Divider />

      {/* --- 3. Лимиты ICU --- */}
      <Title level={5}>Особенности расчёта для палат интенсивной терапии</Title>
      <Paragraph>
        Для палат интенсивной терапии (взрослых и детских) СП 158.13330.2014
        устанавливает верхний предел суточного расхода кислорода:
      </Paragraph>
      <ul style={{ marginLeft: 20 }}>
        <li>
          при количестве коек <Text strong>до 12</Text> — общий расход не должен
          превышать круглосуточную потребность <Text strong>6 коек</Text>;
        </li>
        <li>
          при количестве коек <Text strong>от 12 до 24</Text> — не более
          круглосуточной потребности <Text strong>12 коек</Text>.
        </li>
      </ul>
      <Paragraph type="secondary">
        Это ограничение применяется к общему суточному расходу кислорода для
        отделений ИТ и используется как корректирующий «потолок» при расчётах.
      </Paragraph>

      <Divider />

      {/* --- 4. Система централизованного снабжения --- */}
      <Title level={5}>Система централизованного кислородоснабжения</Title>
      <Paragraph>
        Система включает:
        <ul style={{ marginLeft: 20, marginTop: 4 }}>
          <li>источник кислородоснабжения (КГС, баллоны, генераторы);</li>
          <li>наружную сеть кислородопроводов;</li>
          <li>внутреннюю систему кислородоснабжения.</li>
        </ul>
      </Paragraph>

      <Paragraph>
        В зависимости от потребности и условий допускается использование:
        <ul style={{ marginLeft: 20, marginTop: 4 }}>
          <li>
            <Text strong>Кислородно-газификационной станции (КГС)</Text> —
            хранение и газификация жидкого кислорода; резервуар ≤16 т, расстояние
            от зданий ≥9 м, площадка из негорючих материалов, запас на ≥5 сут.
          </li>
          <li>
            <Text strong>Центрального кислородного пункта</Text> — помещение для
            40 л баллонов при количестве ≥10 шт. (бетон/кирпич, t ≥ 10 °C,
            расстояние ≥12 м от зданий). Две рампы — рабочая и резервная.
          </li>
          <li>
            <Text strong>Кислородных генераторов (концентраторов)</Text> — для
            случаев ограниченной площадки или отсутствия поставок жидкого
            кислорода; чистота кислорода (93 ± 3) %, давление до 0.8 МПа.
          </li>
        </ul>
      </Paragraph>

      <Divider />

      {/* --- 5. Наружные и внутренние сети --- */}
      <Title level={5}>Наружные и внутренние сети кислородопроводов</Title>
      <Paragraph>
        <ul style={{ marginLeft: 20 }}>
          <li>
            Давление в наружных сетях — до <Text strong>1.6 МПа</Text>, скорость
            движения — до 50 м/с.
          </li>
          <li>
            Глубина заложения подземных трубопроводов — не менее 0.6–0.9 м.
          </li>
          <li>
            Материалы — коррозионно-стойкая сталь (ГОСТ 9941) или медь (ГОСТ 617);
            толщина стенки ≥ 3 мм.
          </li>
          <li>
            Обязательно дублирование подземных линий (рабочая + резервная).
          </li>
          <li>
            Во внутренних сетях рабочее давление кислорода —{" "}
            <Text strong>0.45 МПа ± 0.05 МПа</Text>, для барозалов — 0.8 МПа.
          </li>
        </ul>
      </Paragraph>

      <Divider />

      {/* --- 6. Требования к арматуре --- */}
      <Title level={5}>Требования к арматуре и оборудованию</Title>
      <Paragraph>
        Для кислородных линий применяются только арматура и соединения,
        предназначенные для кислорода (латунь, бронза, нержавеющая сталь).
        Использование стальной или чугунной арматуры не допускается.
      </Paragraph>

      <Divider />

      {/* --- 7. Область подачи кислорода --- */}
      <Title level={5}>Помещения, в которых предусматривается подводка кислорода</Title>
      <Paragraph>
        Операционные, наркозные, реанимации, барокамеры, предродовые и родовые
        палаты, послеродовые и послеоперационные палаты, палаты интенсивной
        терапии, перевязочные, процедурные, эндоскопии, ангиографии, палаты на
        1–2 койки, для новорождённых и недоношенных детей и др. — согласно
        технологическому заданию.
      </Paragraph>

      <Divider />

      {/* --- 8. Таблица --- */}
      <Paragraph>
        <Text strong>Нормативные параметры расхода по типам помещений:</Text>
      </Paragraph>

      <GasDocsTable gasKey="oxygen" />

      <Paragraph
        type="secondary"
        style={{
          marginTop: 16,
          fontStyle: "italic",
          fontSize: 13,
          lineHeight: 1.6,
        }}
      >
        Значения приведены по СП 158.13330.2014 и могут уточняться в зависимости
        от назначения помещения, числа коек, режима работы и особенностей
        оборудования. Для ОИТ и реанимаций применяется ограничение на суточный
        расход — не более круглосуточной потребности 6 или 12 коек.
      </Paragraph>
    </Card>
  );
}
